import React from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import { Dialog } from '../../../components/dialog';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas } from '../../../local-types';
import FormInput from '../../../components/form/formInput';
// Modules
import { actions as billActions, types as billTypes } from '../../../redux/reducers/bills';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { selectors as errorSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import withGlobalStyles from '../../../utils/global-styles';
import { FileUpload, RectificationAddPayload } from '../../../redux/reducers/api-types';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
// Ressources
const queryString = require('query-string');

interface RectificationAddProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    saveRectification: (id: string, payload: RectificationAddPayload, callback: React.EffectCallback) => any;
    saveRectificationError: ErrorPayload;
    isSavingRectification: boolean;
}

interface RectificationAddState {
    formData: any;
}
interface RectificationAddFormData {
    bill_number: string;
    transport: number;
    management_fees_month?: number;
    extra_no_tax?: number;
    adjustment_transport?: number;
    adjustment_service?: number;
    adjustment_no_tax?: number;
    pdf: any;
}

class RectificationAddPage extends React.Component<RectificationAddProps, RectificationAddState> {
    inputs: FormDatas[];
    file: any;
    filterParams: any;
    myDialogRef: any;

    constructor(props: RectificationAddProps) {
        super(props);
        const { t } = props;

        this.filterParams = queryString.parse(this.props.location.search);
        const { bill_number, id /*, type*/ } = this.filterParams;

        this.state = {
            formData: {
                id,
                bill_number,
            },
        };

        this.file = null;

        this.inputs = [
            {
                title: t('rectification.new'),
                datas: [
                    {
                        id: 'bill_number',
                        label: t('bill_id'),
                        required: true,
                        disable: true,
                    },
                    {
                        id: 'transport',
                        label: `${t('rectification.amount.transport')} (${t('common:euro_ht')})`,
                        type: 'number',
                        numberNegative: false,
                        required: true,
                    },
                    {
                        id: 'management_fees_month',
                        label: `${t('rectification.amount.management_fees_month')} (${t('common:euro_ht')})`,
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'extra_no_tax',
                        label: `${t('rectification.amount.extra_no_tax')} (${t('common:euro_ht')})`,
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'adjustment_transport',
                        label: `${t('rectification.amount.adjustment_transport')} (${t('common:euro_ht')})`,
                        hint: `${t('rectification.amount.hint_negative')}`,
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'adjustment_service',
                        label: `${t('rectification.amount.adjustment_service')} (${t('common:euro_ht')})`,
                        hint: `${t('rectification.amount.hint_negative')}`,
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'adjustment_no_tax',
                        label: `${t('rectification.amount.adjustment_no_tax')} (${t('common:euro_ht')})`,
                        hint: `${t('rectification.amount.hint_negative')}`,
                        type: 'number',
                        numberNegative: false,
                    },
                    {
                        id: 'pdf',
                        label: `${t('rectification.pdf')}`,
                        type: 'file',
                        hint: `${t('rectification.pdf_hint')}`,
                        customView: (val, handleChange, formData, hint) => {
                            return (
                                <FormInput
                                    id="pdf"
                                    name="pdf"
                                    type="file"
                                    label={t('rectification.pdf')}
                                    value={val}
                                    hint={hint}
                                    inputProps={{ accept: '.pdf' }}
                                    handleChange={(e) => {
                                        handleChange(e);
                                        const files: FileUpload[] = this.handleDocumentChange(e);
                                        const file: FileUpload = files[0];
                                        if (file) {
                                            const fr = new FileReader();
                                            fr.onload = (event: ProgressEvent<FileReader>) => {
                                                this.file = btoa(event.target.result as string);
                                            };
                                            fr.readAsBinaryString(file.file);
                                        }
                                    }}
                                />
                            );
                        },
                    },
                ],
            },
        ];
    }

    handleDocumentChange = (e) => {
        if (e.target.files.length) {
            const arrFiles = Array.from(e.target.files);
            const files: FileUpload[] = arrFiles.map((file: any, index) => {
                const src = window.URL.createObjectURL(file);
                return { file, id: index, src };
            });
            return files;
        }
        return [];
    };

    submitExceptional = (): void => {
        const { formData } = this.state;
        const { saveRectification, history } = this.props;
        const payload = this.generatePayload(formData);
        // console.log('submitting', payload);
        saveRectification(formData.id, payload, () => {
            history.push(paths.billsRectifications);
        });
    };

    generatePayload = (formData: RectificationAddFormData): RectificationAddPayload => {
        const negative = ['adjustment_transport', 'adjustment_service'];
        const accepted = [
            'transport',
            'management_fees_month',
            'extra_no_tax',
            'adjustment_transport',
            'adjustment_service',
            'adjustment_no_tax',
        ];

        const products = Object.keys(formData)
            .filter((e) => formData[e] && accepted.includes(e))
            .map((p) => {
                let value = formData[p];
                if (negative.includes(p)) {
                    value = -value;
                }
                return { type: p, amount: value };
            });

        const payload: RectificationAddPayload = {
            products,
        };

        if (this.file) {
            payload.additional_pages = this.file;
        }

        return payload;
    };

    handleSubmit = (formData, event): void => {
        this.setState({
            formData: formData,
        });
        this.myDialogRef.showDialog();
    };

    render() {
        const { t, globalClasses, isSavingRectification } = this.props;
        const formPayload = this.state.formData;

        return (
            <App needRights={{ name: AccountRight.BILL_RECTIFICATION, mode: RightRank.W }}>
                <Dialog
                    onRef={(ref) => (this.myDialogRef = ref)}
                    btnTitle={t('rectification.validation_popup.title')}
                    description={t('rectification.validation_popup.msg')}
                    customButton={<></>}
                    onOK={this.submitExceptional}
                />
                <>
                    <p className={globalClasses.block} style={{ color: 'red' }}>
                        {t('rectification.warning')}
                    </p>
                    <ErrorMessage payload={this.props.saveRectificationError} />
                    <EnhancedForm
                        payload={formPayload}
                        inputs={this.inputs}
                        backRoute={paths.billsRectifications}
                        onSubmit={this.handleSubmit}
                        submitText={t('rectification.validation_popup.title')}
                        loader={isSavingRectification}
                    />
                </>
            </App>
        );
    }
}

const RectificationAddPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['bill', 'common'])(RectificationAddPage),
);

const mapStateToProps = (state) => ({
    saveRectificationError: errorSelectors.saveRectificationError(state),
    isSavingRectification: pendingSelectors.isPending(state, billTypes.CREATE_BILLS_RECTIFICATION),
});

const mapDispatchToProps = {
    saveRectification: billActions.saveRectification,
};

export default connect(mapStateToProps, mapDispatchToProps)(RectificationAddPageWithStyleAndTranslation);
